var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { PropertyService } from '@data/api';
import { isNetworkOrCorsError } from '@data/api/errors';
import Store from '@data/stores/Store';
// Import from the submodule (not the '@business/utils' barrel): the barrel
// re-exports payment.ts, and pulling it in here creates a circular chain
// (payment -> stores/Store -> stores/Property -> utils barrel -> payment) that
// react-refresh trips over as a TDZ error when the payment popup boots (BENG-4169).
import { isInIframe } from '@business/utils/window';
import Accommodations from '../Accommodations';
import Addons from '../Addons';
import Amplify from '../Amplify';
import AmenityStore from '../Amenities';
import AvailabilityCalendar from '../AvailabilityCalendar';
import Cart from '../Cart';
import CustomPaymentOption from '../CustomPaymentOption';
import Guests from '../Guests';
import Payment from '../Payment';
import Reservation from '../Reservation';
import TravelInsuranceStore from '../TravelInsurance';
export default class Property {
    constructor() {
        Object.defineProperty(this, "accommodations", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Accommodations()
        });
        Object.defineProperty(this, "addons", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Addons()
        });
        Object.defineProperty(this, "amenities", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new AmenityStore()
        });
        Object.defineProperty(this, "amplify", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Amplify()
        });
        Object.defineProperty(this, "availabilityCalendar", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new AvailabilityCalendar()
        });
        Object.defineProperty(this, "cart", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Cart()
        });
        Object.defineProperty(this, "customPaymentOption", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "guests", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Guests()
        });
        Object.defineProperty(this, "hasImmersiveLoadCorsError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "model", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "payment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Payment()
        });
        Object.defineProperty(this, "reservation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Reservation()
        });
        Object.defineProperty(this, "settings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                defaultFilteredOccupantsToTwo: false,
                defaultRoomCardState: 'collapsed',
            }
        });
        Object.defineProperty(this, "travelInsurance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new TravelInsuranceStore()
        });
        Object.defineProperty(this, "loadProperty", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (request) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                runInAction(() => {
                    this.model = null;
                    this.hasImmersiveLoadCorsError = false;
                    this.settings = {
                        defaultFilteredOccupantsToTwo: false,
                        defaultRoomCardState: 'collapsed',
                    };
                });
                try {
                    const property = yield PropertyService.loadProperty(Object.assign(Object.assign({}, request), { bookingEngineSource: (_a = request.bookingEngineSource) !== null && _a !== void 0 ? _a : Store.environment.bookingEngineSource, iframe: isInIframe() }));
                    runInAction(() => {
                        this.model = property;
                        this.settings = property.settings;
                    });
                    return property;
                }
                catch (error) {
                    // CORS / network failure (e.g. IE embedded on a non-whitelisted domain).
                    // Surface it so Root can render the deactivated card instead of a blank
                    // screen; re-throw so existing non-CORS error handling is preserved.
                    if (isNetworkOrCorsError(error)) {
                        runInAction(() => {
                            this.hasImmersiveLoadCorsError = true;
                        });
                    }
                    throw error;
                }
            })
        });
        Object.defineProperty(this, "setCustomPaymentOption", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (customPaymentOption) => runInAction(() => {
                this.customPaymentOption = new CustomPaymentOption(customPaymentOption);
            })
        });
        makeObservable(this);
    }
    get storageKey() {
        return 'property';
    }
    load() {
        this.accommodations.load();
        this.addons.load();
        this.amenities.load();
        this.cart.load();
        this.guests.load();
        this.travelInsurance.load();
    }
    get language() {
        return Store.environment.language;
    }
    get description() {
        var _a;
        return (_a = this.model) === null || _a === void 0 ? void 0 : _a.description.getText(this.language);
    }
    get hasCancellationPolicies() {
        var _a;
        const { useCustomPolicy, customPolicy, standardizedPolicies } = ((_a = this.model) === null || _a === void 0 ? void 0 : _a.cancellationPolicies) || {};
        const { fullCharge, partialCharge, noCharge } = standardizedPolicies || {};
        return ((useCustomPolicy && customPolicy && customPolicy.has(this.language)) ||
            (!useCustomPolicy &&
                ((fullCharge === null || fullCharge === void 0 ? void 0 : fullCharge.allowed) || (partialCharge === null || partialCharge === void 0 ? void 0 : partialCharge.allowed) || (noCharge === null || noCharge === void 0 ? void 0 : noCharge.allowed))));
    }
    get hasTermsAndConditions() {
        var _a, _b;
        return (_b = (_a = this.model) === null || _a === void 0 ? void 0 : _a.termsAndConditions) === null || _b === void 0 ? void 0 : _b.has(this.language);
    }
    // True when the property has non-empty T&C content for its DEFAULT language.
    // Drives the standalone T&C link visibility (BENG-4232): a missing key or an
    // empty string counts as no policy. Deliberately independent of the guest's
    // viewing language — the property owns entering content in its default
    // language, and the standalone page keeps its own default-language fallback.
    get hasTermsAndConditionsInDefaultLanguage() {
        var _a;
        return !!(this.model &&
            ((_a = this.model.termsAndConditions) === null || _a === void 0 ? void 0 : _a.has(this.model.defaultLanguage)));
    }
    get termsAndConditions() {
        var _a;
        return (_a = this.model) === null || _a === void 0 ? void 0 : _a.termsAndConditions.getText(this.language);
    }
    // True when the property has non-empty Privacy Policy content for its DEFAULT
    // language. Mirrors hasTermsAndConditionsInDefaultLanguage (BENG-4232).
    get hasPrivacyPolicyInDefaultLanguage() {
        var _a;
        return !!(this.model && ((_a = this.model.privacyPolicy) === null || _a === void 0 ? void 0 : _a.has(this.model.defaultLanguage)));
    }
    save() {
        this.accommodations.save();
        this.addons.save();
        this.amenities.save();
        this.cart.save();
        this.guests.save();
        this.travelInsurance.save();
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], Property.prototype, "hasImmersiveLoadCorsError", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Property.prototype, "model", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Property.prototype, "settings", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Property.prototype, "language", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Property.prototype, "description", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Property.prototype, "hasCancellationPolicies", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Property.prototype, "hasTermsAndConditions", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Property.prototype, "hasTermsAndConditionsInDefaultLanguage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Property.prototype, "termsAndConditions", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Property.prototype, "hasPrivacyPolicyInDefaultLanguage", null);
